/**
 * EnergyPlus Example File Index
 *
 * Comprehensive catalog of EnergyPlus example files with rich metadata
 * for search, filtering, and learning guidance.
 *
 * Examples are sourced from NREL's EnergyPlus repository.
 */

import type { ExampleFileMetadata, ExampleFilterFacets } from '../types';

/**
 * Complete index of EnergyPlus example files.
 * These correspond to actual files in /public/examples/energyplus/
 */
export const EXAMPLE_FILES: ExampleFileMetadata[] = [
  // ============ TUTORIALS ============
  {
    id: '1zone-uncontrolled',
    name: '1 Zone Uncontrolled',
    filename: '1ZoneUncontrolled.idf',
    description: 'The simplest possible EnergyPlus model - a single zone with no HVAC. Perfect starting point for understanding basic geometry and thermal simulation.',
    category: 'tutorials',
    buildingType: 'generic',
    complexity: 'beginner',
    objectsShowcased: {
      primary: ['Building', 'Zone', 'BuildingSurface:Detailed', 'Material', 'Construction'],
      secondary: ['SimulationControl', 'RunPeriod', 'Output:Variable'],
    },
    tags: ['single-zone', 'no-hvac', 'beginner', 'geometry', 'thermal-mass'],
    stats: { zones: 1, surfaces: 6, objectCount: 45 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Learn the fundamental structure of an EnergyPlus model with no HVAC complexity.',
      concepts: ['geometry-basics', 'zone-definition', 'constructions', 'heat-balance'],
      steps: [
        {
          title: 'Understanding the Building Object',
          description: 'The Building object sets global parameters like terrain, solar distribution, and building rotation.',
          focusObjects: ['Building'],
          tips: ['Terrain affects wind exposure calculations', 'North axis rotation can be used for orientation studies'],
        },
        {
          title: 'Zone Definition',
          description: 'Zones are the basic thermal units in EnergyPlus. Each zone has its own heat balance.',
          focusObjects: ['Zone'],
          tips: ['Zone volume is calculated from surfaces if not specified', 'Multipliers can represent identical zones'],
        },
        {
          title: 'Surface Geometry',
          description: 'BuildingSurface:Detailed defines walls, floors, and roofs using vertex coordinates.',
          focusObjects: ['BuildingSurface:Detailed'],
          tips: ['Vertices must be specified counter-clockwise when viewed from outside', 'Use consistent units (meters)'],
        },
      ],
      commonMistakes: [
        'Forgetting to define all 6 surfaces for a closed zone',
        'Incorrect vertex ordering causing inward-facing surfaces',
        'Missing constructions on surfaces',
      ],
      tryThis: [
        'Add a window to one of the walls',
        'Change the construction to a higher insulation value',
        'Add internal mass to see thermal lag effects',
      ],
      relatedExamples: ['1zone-with-window', '1zone-evap-cooler'],
    },
    idfUrl: '/examples/energyplus/1ZoneUncontrolled.idf',
    weatherFile: { name: 'Denver, CO (TMY3)', climateZone: '5B' },
    popularity: 95,
  },
  {
    id: '1zone-with-window',
    name: '1 Zone with Window',
    filename: '1ZoneUncontrolled_win_1.idf',
    description: 'Single zone building with a window - learn how to add fenestration and understand solar heat gains.',
    category: 'tutorials',
    buildingType: 'generic',
    complexity: 'beginner',
    objectsShowcased: {
      primary: ['FenestrationSurface:Detailed', 'WindowMaterial:SimpleGlazingSystem', 'BuildingSurface:Detailed'],
      secondary: ['Construction', 'Material', 'Output:Variable'],
    },
    tags: ['single-zone', 'windows', 'beginner', 'solar-gains', 'glazing'],
    stats: { zones: 1, surfaces: 7, objectCount: 55 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Learn how to add windows and understand solar heat gains in a simple building.',
      prerequisites: ['1zone-uncontrolled'],
      concepts: ['geometry-basics', 'solar-gains', 'constructions'],
      steps: [
        {
          title: 'Adding Windows',
          description: 'FenestrationSurface:Detailed defines windows on wall surfaces using vertex coordinates.',
          focusObjects: ['FenestrationSurface:Detailed'],
          tips: ['Window vertices must lie on the host surface plane', 'U-factor and SHGC are key thermal properties'],
        },
        {
          title: 'Window Materials',
          description: 'SimpleGlazingSystem lets you specify U-factor and SHGC directly without detailed layers.',
          focusObjects: ['WindowMaterial:SimpleGlazingSystem'],
          tips: ['Great for early design studies', 'Use detailed glazing for more accurate results'],
        },
      ],
      tryThis: [
        'Change the window SHGC and observe cooling load changes',
        'Add windows to other orientations',
        'Compare simple vs detailed glazing properties',
      ],
      relatedExamples: ['1zone-uncontrolled', '1zone-evap-cooler', 'window-tests'],
    },
    idfUrl: '/examples/energyplus/1ZoneUncontrolled_win_1.idf',
    weatherFile: { name: 'Denver, CO (TMY3)', climateZone: '5B' },
    popularity: 90,
  },
  {
    id: '1zone-evap-cooler',
    name: '1 Zone Evaporative Cooler',
    filename: '1ZoneEvapCooler.idf',
    description: 'Single zone with evaporative cooling - introduction to HVAC concepts and zone equipment.',
    category: 'tutorials',
    buildingType: 'generic',
    complexity: 'beginner',
    hvacSystems: ['evaporative'],
    objectsShowcased: {
      primary: ['ZoneHVAC:EvaporativeCoolerUnit', 'EvaporativeCooler:Direct:CelDekPad', 'Fan:ConstantVolume'],
      secondary: ['Schedule:Compact', 'ZoneControl:Thermostat'],
    },
    tags: ['evaporative-cooling', 'single-zone', 'beginner', 'hvac-intro', 'dry-climate'],
    stats: { zones: 1, surfaces: 6, hvacSystems: 1, objectCount: 75 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Learn HVAC basics with evaporative cooling - a simple, energy-efficient system for dry climates.',
      prerequisites: ['1zone-uncontrolled'],
      concepts: ['hvac-basics', 'schedules', 'zone-definition'],
      steps: [
        {
          title: 'Zone Equipment Setup',
          description: 'Zone equipment is connected to zones through ZoneHVAC:EquipmentList and ZoneHVAC:EquipmentConnections.',
          focusObjects: ['ZoneHVAC:EquipmentList', 'ZoneHVAC:EquipmentConnections'],
          tips: ['Equipment connections define inlet and outlet nodes', 'Multiple equipment can serve a zone'],
        },
        {
          title: 'Evaporative Cooler',
          description: 'Direct evaporative coolers add moisture while cooling - effective in dry climates.',
          focusObjects: ['EvaporativeCooler:Direct:CelDekPad'],
          tips: ['Effectiveness depends on wet bulb depression', 'Works best in hot, dry climates'],
        },
        {
          title: 'Thermostat Control',
          description: 'Thermostats control when equipment turns on based on zone temperature.',
          focusObjects: ['ZoneControl:Thermostat'],
        },
      ],
      tryThis: [
        'Change the cooling setpoint temperature',
        'Test in different climate zones',
        'Add a second-stage cooling option',
      ],
      relatedExamples: ['1zone-uncontrolled', '5zone-air-cooled'],
    },
    idfUrl: '/examples/energyplus/1ZoneEvapCooler.idf',
    weatherFile: { name: 'Phoenix, AZ (TMY3)', climateZone: '2B' },
    popularity: 88,
  },

  // ============ HVAC SYSTEMS ============
  {
    id: '5zone-air-cooled',
    name: '5 Zone Air Cooled',
    filename: '5ZoneAirCooled.idf',
    description: 'A 5-zone building with a packaged single-zone air system. Classic example for understanding multi-zone buildings and basic air systems.',
    category: 'hvac-systems',
    buildingType: 'office-small',
    complexity: 'intermediate',
    hvacSystems: ['packaged'],
    objectsShowcased: {
      primary: ['AirLoopHVAC', 'AirLoopHVAC:UnitarySystem', 'Coil:Cooling:DX:SingleSpeed', 'Coil:Heating:Fuel'],
      secondary: ['Fan:OnOff', 'ZoneHVAC:AirDistributionUnit', 'AirTerminal:SingleDuct:Uncontrolled'],
    },
    tags: ['multi-zone', 'packaged-unit', 'dx-cooling', 'gas-heating', 'air-loop'],
    stats: { zones: 5, surfaces: 40, hvacSystems: 1, objectCount: 180 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Learn how to model a complete air-side HVAC system serving multiple zones with a packaged rooftop unit.',
      prerequisites: ['1zone-evap-cooler'],
      concepts: ['air-loops', 'hvac-basics', 'sizing', 'setpoint-managers'],
      steps: [
        {
          title: 'Understanding Air Loops',
          description: 'AirLoopHVAC defines the supply and return air path for an air handling system.',
          focusObjects: ['AirLoopHVAC', 'AirLoopHVAC:OutdoorAirSystem'],
          tips: ['Supply path goes from AHU to zones', 'Return path brings air back to AHU'],
        },
        {
          title: 'DX Cooling Coil',
          description: 'Direct expansion cooling coils use refrigerant to cool air directly.',
          focusObjects: ['Coil:Cooling:DX:SingleSpeed'],
          tips: ['COP varies with outdoor temperature', 'Performance curves are critical'],
        },
        {
          title: 'Zone Air Distribution',
          description: 'Air terminals control airflow to each zone.',
          focusObjects: ['AirTerminal:SingleDuct:Uncontrolled', 'ZoneHVAC:AirDistributionUnit'],
        },
      ],
      tryThis: [
        'Add an economizer to the outdoor air system',
        'Change to a two-speed DX coil',
        'Add zone-level reheat',
      ],
      relatedExamples: ['5zone-vav', '5zone-auto-dx-vav'],
    },
    idfUrl: '/examples/energyplus/5ZoneAirCooled.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 92,
  },
  {
    id: '5zone-auto-dx-vav',
    name: '5 Zone AutoDX VAV',
    filename: '5ZoneAutoDXVAV.idf',
    description: 'VAV system with DX cooling - variable air volume with direct expansion coils for efficient part-load operation.',
    category: 'hvac-systems',
    buildingType: 'office-small',
    complexity: 'intermediate',
    hvacSystems: ['vav', 'packaged'],
    objectsShowcased: {
      primary: ['AirLoopHVAC', 'AirTerminal:SingleDuct:VAV:NoReheat', 'Coil:Cooling:DX:SingleSpeed'],
      secondary: ['Fan:VariableVolume', 'Controller:OutdoorAir'],
    },
    tags: ['vav', 'dx-cooling', 'variable-speed', 'energy-efficient'],
    stats: { zones: 5, surfaces: 40, hvacSystems: 1, objectCount: 160 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Learn VAV fundamentals with DX cooling - combines variable airflow with efficient direct-expansion cooling.',
      prerequisites: ['5zone-air-cooled'],
      concepts: ['air-loops', 'hvac-basics', 'sizing'],
      steps: [
        {
          title: 'VAV Terminal Units',
          description: 'VAV terminals modulate airflow based on zone load.',
          focusObjects: ['AirTerminal:SingleDuct:VAV:NoReheat'],
          tips: ['Minimum flow settings affect ventilation', 'Can add reheat for perimeter zones'],
        },
        {
          title: 'Variable Speed Fan',
          description: 'Variable volume fans save energy at part load.',
          focusObjects: ['Fan:VariableVolume'],
          tips: ['Fan power varies with cube of flow', 'VSDs provide better part-load efficiency'],
        },
      ],
      relatedExamples: ['5zone-vav', '5zone-air-cooled'],
    },
    idfUrl: '/examples/energyplus/5ZoneAutoDXVAV.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 85,
  },
  {
    id: '5zone-vav',
    name: '5 Zone VAV with Central Plant',
    filename: '5ZoneVAV-Pri-SecLoop.idf',
    description: 'Variable Air Volume system with chilled water cooling and hot water heating - complete with primary-secondary plant loops.',
    category: 'hvac-systems',
    buildingType: 'office-small',
    complexity: 'advanced',
    hvacSystems: ['vav'],
    plantEquipment: ['chiller', 'boiler', 'cooling-tower'],
    objectsShowcased: {
      primary: ['AirLoopHVAC', 'AirTerminal:SingleDuct:VAV:Reheat', 'Coil:Cooling:Water', 'Coil:Heating:Water'],
      secondary: ['Chiller:Electric:EIR', 'Boiler:HotWater', 'PlantLoop', 'Pump:VariableSpeed'],
    },
    tags: ['vav', 'reheat', 'chiller', 'boiler', 'plant-loops', 'commercial'],
    stats: { zones: 5, surfaces: 40, hvacSystems: 1, plantLoops: 3, objectCount: 320 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Master VAV system modeling with chilled water cooling, hot water reheat, and complete primary-secondary plant loops.',
      prerequisites: ['5zone-air-cooled'],
      concepts: ['air-loops', 'plant-loops', 'setpoint-managers', 'sizing'],
      steps: [
        {
          title: 'VAV Terminal Units',
          description: 'VAV terminals modulate airflow based on zone load, with reheat for heating.',
          focusObjects: ['AirTerminal:SingleDuct:VAV:Reheat'],
          tips: ['Minimum flow fraction affects energy use', 'Reheat can be electric or hot water'],
        },
        {
          title: 'Primary-Secondary Loops',
          description: 'Separate loops for production (primary) and distribution (secondary) equipment.',
          focusObjects: ['PlantLoop', 'Pump:VariableSpeed'],
          tips: ['Common pipe connects primary and secondary', 'Allows independent pump control'],
        },
        {
          title: 'Chilled Water Loop',
          description: 'The chilled water loop connects the chiller to cooling coils.',
          focusObjects: ['PlantLoop', 'Chiller:Electric:EIR'],
        },
        {
          title: 'Hot Water Loop',
          description: 'The hot water loop provides heating to reheat coils.',
          focusObjects: ['PlantLoop', 'Boiler:HotWater'],
        },
      ],
      commonMistakes: [
        'Forgetting to connect all plant loop branches',
        'Incorrect setpoint manager placement',
        'Undersizing pumps or pipes',
      ],
      relatedExamples: ['hvac-template-vav', 'doas-vrf'],
    },
    idfUrl: '/examples/energyplus/5ZoneVAV-Pri-SecLoop.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 90,
  },
  {
    id: 'hvac-template-vav',
    name: 'HVACTemplate VAV Water Cooled',
    filename: 'HVACTemplate-5ZoneVAVWaterCooled.idf',
    description: 'VAV system created using HVACTemplate objects - a faster way to define common HVAC systems.',
    category: 'hvac-systems',
    buildingType: 'office-small',
    complexity: 'intermediate',
    hvacSystems: ['vav'],
    plantEquipment: ['chiller', 'boiler', 'cooling-tower'],
    objectsShowcased: {
      primary: ['HVACTemplate:Zone:VAV', 'HVACTemplate:System:VAV', 'HVACTemplate:Plant:ChilledWaterLoop'],
      secondary: ['HVACTemplate:Plant:HotWaterLoop', 'HVACTemplate:Plant:Chiller', 'HVACTemplate:Plant:Boiler'],
    },
    tags: ['hvac-template', 'vav', 'rapid-modeling', 'commercial'],
    stats: { zones: 5, surfaces: 40, hvacSystems: 1, plantLoops: 3, objectCount: 120 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Learn to use HVACTemplate objects for rapid HVAC modeling - EnergyPlus expands these to full detailed objects.',
      concepts: ['hvac-basics', 'sizing'],
      steps: [
        {
          title: 'HVACTemplate Approach',
          description: 'HVACTemplate objects are a shorthand for common HVAC configurations.',
          focusObjects: ['HVACTemplate:Zone:VAV', 'HVACTemplate:System:VAV'],
          tips: ['EnergyPlus expands templates during preprocessing', 'Good for rapid prototyping'],
        },
        {
          title: 'Plant Templates',
          description: 'Plant templates define chilled water, hot water, and condenser loops.',
          focusObjects: ['HVACTemplate:Plant:ChilledWaterLoop', 'HVACTemplate:Plant:Chiller'],
        },
      ],
      tryThis: [
        'Compare template output to detailed VAV example',
        'Customize template parameters',
        'Add economizer controls',
      ],
      relatedExamples: ['5zone-vav', '5zone-air-cooled'],
    },
    idfUrl: '/examples/energyplus/HVACTemplate-5ZoneVAVWaterCooled.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 82,
  },
  {
    id: '5zone-water-loop-hp',
    name: '5 Zone Water Loop Heat Pump',
    filename: '5ZoneWaterLoopHeatPump.idf',
    description: 'Water source heat pump system with shared condenser loop - efficient for buildings with simultaneous heating and cooling.',
    category: 'hvac-systems',
    buildingType: 'office-small',
    complexity: 'advanced',
    hvacSystems: ['heat-pump'],
    plantEquipment: ['cooling-tower', 'boiler'],
    objectsShowcased: {
      primary: ['ZoneHVAC:WaterToAirHeatPump', 'Coil:Cooling:WaterToAirHeatPump:EquationFit', 'Coil:Heating:WaterToAirHeatPump:EquationFit'],
      secondary: ['PlantLoop', 'CoolingTower:SingleSpeed', 'Boiler:HotWater'],
    },
    tags: ['water-loop-hp', 'heat-pump', 'heat-recovery', 'commercial'],
    stats: { zones: 5, surfaces: 40, hvacSystems: 5, plantLoops: 1, objectCount: 280 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Learn water source heat pump systems that share a condenser loop for energy-efficient heating and cooling.',
      concepts: ['hvac-basics', 'plant-loops', 'sizing'],
      steps: [
        {
          title: 'Water-to-Air Heat Pumps',
          description: 'Each zone has its own heat pump unit connected to a shared water loop.',
          focusObjects: ['ZoneHVAC:WaterToAirHeatPump'],
          tips: ['Each unit provides independent zone control', 'Heat rejected by cooling can be used for heating'],
        },
        {
          title: 'Condenser Loop',
          description: 'The shared water loop connects all heat pumps to central heat rejection/addition.',
          focusObjects: ['PlantLoop', 'CoolingTower:SingleSpeed', 'Boiler:HotWater'],
          tips: ['Loop temperature maintained within limits', 'Boiler and tower provide backup'],
        },
      ],
      relatedExamples: ['gshp', 'doas-vrf'],
    },
    idfUrl: '/examples/energyplus/5ZoneWaterLoopHeatPump.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 78,
  },
  {
    id: 'doas-vrf',
    name: 'DOAS with VRF',
    filename: 'DOAToVRF.idf',
    description: 'Dedicated Outdoor Air System with Variable Refrigerant Flow - modern high-efficiency commercial system.',
    category: 'hvac-systems',
    buildingType: 'office-small',
    complexity: 'advanced',
    hvacSystems: ['doas', 'vrf'],
    objectsShowcased: {
      primary: ['AirConditioner:VariableRefrigerantFlow', 'ZoneHVAC:TerminalUnit:VariableRefrigerantFlow', 'AirLoopHVAC:DedicatedOutdoorAirSystem'],
      secondary: ['Coil:Cooling:DX:VariableRefrigerantFlow', 'Coil:Heating:DX:VariableRefrigerantFlow'],
    },
    tags: ['doas', 'vrf', 'heat-recovery', 'high-efficiency', 'modern'],
    stats: { zones: 5, surfaces: 40, hvacSystems: 2, objectCount: 280 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Model a DOAS with VRF system - separating ventilation from zone conditioning for efficiency and control.',
      concepts: ['air-loops', 'hvac-basics', 'sizing'],
      steps: [
        {
          title: 'VRF System',
          description: 'VRF systems use variable refrigerant flow for efficient zone-level conditioning.',
          focusObjects: ['AirConditioner:VariableRefrigerantFlow', 'ZoneHVAC:TerminalUnit:VariableRefrigerantFlow'],
          tips: ['Heat recovery allows simultaneous heating and cooling', 'Excellent part-load efficiency'],
        },
        {
          title: 'DOAS Integration',
          description: 'The DOAS handles 100% outdoor air for ventilation, separate from zone conditioning.',
          focusObjects: ['AirLoopHVAC:DedicatedOutdoorAirSystem'],
          tips: ['DOAS typically includes energy recovery', 'Neutral supply air temperature common'],
        },
      ],
      tryThis: [
        'Compare energy with and without heat recovery',
        'Test different DOAS supply temperatures',
        'Add enthalpy recovery to DOAS',
      ],
      relatedExamples: ['5zone-vav', '5zone-water-loop-hp'],
    },
    idfUrl: '/examples/energyplus/DOAToVRF.idf',
    weatherFile: { name: 'Seattle, WA (TMY3)', climateZone: '4C' },
    popularity: 85,
  },
  {
    id: 'gshp',
    name: 'Ground Source Heat Pump',
    filename: 'GSHP-GLHE.idf',
    description: 'Ground source heat pump system with vertical borehole ground loop heat exchanger - highly efficient year-round.',
    category: 'hvac-systems',
    buildingType: 'office-small',
    complexity: 'advanced',
    hvacSystems: ['heat-pump'],
    plantEquipment: ['ground-loop'],
    objectsShowcased: {
      primary: ['GroundHeatExchanger:System', 'HeatPump:WaterToWater:EquationFit:Cooling', 'HeatPump:WaterToWater:EquationFit:Heating'],
      secondary: ['PlantLoop', 'Site:GroundTemperature:Undisturbed:KusudaAchenbach'],
    },
    tags: ['gshp', 'ground-loop', 'borehole', 'heat-pump', 'renewable', 'geothermal'],
    stats: { zones: 3, surfaces: 24, hvacSystems: 1, plantLoops: 2, objectCount: 200 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Model a ground source heat pump system with vertical boreholes for high efficiency heating and cooling.',
      concepts: ['plant-loops', 'ground-coupling', 'sizing'],
      steps: [
        {
          title: 'Ground Heat Exchanger',
          description: 'The borehole field provides stable source temperatures year-round.',
          focusObjects: ['GroundHeatExchanger:System'],
          tips: ['Borehole spacing affects long-term performance', 'Ground thermal properties are critical inputs'],
        },
        {
          title: 'Undisturbed Ground Temperature',
          description: 'The Kusuda-Achenbach model calculates ground temperature at depth.',
          focusObjects: ['Site:GroundTemperature:Undisturbed:KusudaAchenbach'],
          tips: ['Average surface temperature and amplitude are key inputs', 'Phase shift accounts for seasonal lag'],
        },
        {
          title: 'Water-to-Water Heat Pumps',
          description: 'Heat pumps connect the ground loop to the building distribution system.',
          focusObjects: ['HeatPump:WaterToWater:EquationFit:Cooling', 'HeatPump:WaterToWater:EquationFit:Heating'],
          tips: ['COP varies with entering water temperature', 'Equation fit model uses manufacturer data'],
        },
      ],
      commonMistakes: [
        'Undersizing the borehole field for long-term operation',
        'Ignoring ground thermal imbalance over time',
        'Using incorrect ground thermal properties',
      ],
      relatedExamples: ['5zone-water-loop-hp', 'doas-vrf'],
    },
    idfUrl: '/examples/energyplus/GSHP-GLHE.idf',
    weatherFile: { name: 'New York, NY (TMY3)', climateZone: '4A' },
    popularity: 80,
  },

  // ============ RESIDENTIAL ============
  {
    id: 'furnace',
    name: 'Residential Furnace',
    filename: 'Furnace.idf',
    description: 'Gas furnace system for residential applications - the most common heating system in US homes.',
    category: 'residential',
    buildingType: 'single-family',
    complexity: 'intermediate',
    hvacSystems: ['unitary'],
    objectsShowcased: {
      primary: ['AirLoopHVAC:UnitarySystem', 'Coil:Heating:Fuel', 'Fan:OnOff'],
      secondary: ['AirLoopHVAC', 'Schedule:Compact', 'ZoneControl:Thermostat'],
    },
    tags: ['furnace', 'gas-heating', 'residential', 'single-family'],
    stats: { zones: 3, surfaces: 24, hvacSystems: 1, objectCount: 120 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Model a residential gas furnace system with typical home zoning and schedules.',
      concepts: ['hvac-basics', 'schedules', 'air-loops'],
      steps: [
        {
          title: 'Furnace System',
          description: 'Gas furnaces provide forced-air heating through a central air handler.',
          focusObjects: ['Coil:Heating:Fuel', 'Fan:OnOff'],
          tips: ['AFUE rating indicates efficiency', 'Two-stage models improve comfort'],
        },
        {
          title: 'Residential Schedules',
          description: 'Occupancy and thermostat schedules differ from commercial buildings.',
          focusObjects: ['Schedule:Compact'],
          tips: ['Consider setback schedules for energy savings', 'Weekend vs weekday patterns'],
        },
      ],
      relatedExamples: ['furnace-with-dx', '5zone-air-cooled'],
    },
    idfUrl: '/examples/energyplus/Furnace.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 75,
  },
  {
    id: 'furnace-with-dx',
    name: 'Furnace with AC',
    filename: 'FurnaceWithDXSystem.idf',
    description: 'Gas furnace with DX cooling - the most common residential HVAC configuration in the US.',
    category: 'residential',
    buildingType: 'single-family',
    complexity: 'intermediate',
    hvacSystems: ['unitary'],
    objectsShowcased: {
      primary: ['AirLoopHVAC:UnitarySystem', 'Coil:Heating:Fuel', 'Coil:Cooling:DX:SingleSpeed'],
      secondary: ['Fan:OnOff', 'AirLoopHVAC', 'ZoneControl:Thermostat'],
    },
    tags: ['furnace', 'air-conditioning', 'residential', 'split-system'],
    stats: { zones: 3, surfaces: 24, hvacSystems: 1, objectCount: 140 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Model a typical residential heating and cooling system with gas furnace and DX air conditioning.',
      prerequisites: ['furnace'],
      concepts: ['hvac-basics', 'sizing', 'schedules'],
      steps: [
        {
          title: 'Combined System',
          description: 'The unitary system combines heating and cooling in one air handler.',
          focusObjects: ['AirLoopHVAC:UnitarySystem'],
          tips: ['DX coil sits above furnace in airflow', 'Shared fan serves both modes'],
        },
        {
          title: 'DX Cooling',
          description: 'Single-speed DX coils are the most common residential cooling.',
          focusObjects: ['Coil:Cooling:DX:SingleSpeed'],
          tips: ['SEER rating indicates efficiency', 'Proper sizing is critical for humidity control'],
        },
      ],
      tryThis: [
        'Change to a heat pump for heating',
        'Add a two-speed cooling coil',
        'Model a mini-split alternative',
      ],
      relatedExamples: ['furnace', '5zone-air-cooled'],
    },
    idfUrl: '/examples/energyplus/FurnaceWithDXSystem.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 82,
  },

  // ============ COMMERCIAL BUILDINGS ============
  {
    id: 'ref-small-office',
    name: 'DOE Reference Small Office',
    filename: 'RefBldgSmallOfficeNew2004_Chicago.idf',
    description: 'DOE Commercial Reference Building - Small Office. Complete building with realistic loads and systems.',
    category: 'commercial',
    buildingType: 'office-small',
    complexity: 'intermediate',
    hvacSystems: ['packaged'],
    objectsShowcased: {
      primary: ['AirLoopHVAC:UnitarySystem', 'People', 'Lights', 'ElectricEquipment'],
      secondary: ['Schedule:Compact', 'ZoneInfiltration:DesignFlowRate'],
    },
    tags: ['reference-building', 'doe', 'small-office', 'benchmark'],
    stats: { zones: 6, surfaces: 50, hvacSystems: 1, floorArea: 511, objectCount: 300 },
    source: { origin: 'DOE', version: '24.1', author: 'PNNL' },
    learning: {
      summary: 'The DOE Reference Small Office represents a typical single-story office building for benchmarking.',
      concepts: ['internal-loads', 'schedules', 'hvac-basics', 'infiltration'],
      steps: [
        {
          title: 'Building Characteristics',
          description: 'Reference buildings have standardized geometry, loads, and schedules.',
          focusObjects: ['Building', 'Zone'],
          tips: ['5 occupied zones plus 1 core zone', 'Typical 2004 code-compliant construction'],
        },
        {
          title: 'Internal Loads',
          description: 'People, lighting, and equipment loads follow realistic schedules.',
          focusObjects: ['People', 'Lights', 'ElectricEquipment'],
          tips: ['Lighting power density: 10.76 W/m2', 'Equipment power density: 10.76 W/m2'],
        },
        {
          title: 'HVAC System',
          description: 'Packaged single-zone system typical for small commercial buildings.',
          focusObjects: ['AirLoopHVAC:UnitarySystem'],
        },
      ],
      relatedExamples: ['ref-medium-office', 'ref-warehouse'],
    },
    idfUrl: '/examples/energyplus/RefBldgSmallOfficeNew2004_Chicago.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 88,
  },
  {
    id: 'ref-medium-office',
    name: 'DOE Reference Medium Office',
    filename: 'RefBldgMediumOfficeNew2004_Chicago.idf',
    description: 'DOE Commercial Reference Building - Medium Office. Three-story building with VAV system and central plant.',
    category: 'commercial',
    buildingType: 'office-medium',
    complexity: 'advanced',
    hvacSystems: ['vav'],
    plantEquipment: ['chiller', 'boiler'],
    objectsShowcased: {
      primary: ['AirLoopHVAC', 'AirTerminal:SingleDuct:VAV:Reheat', 'Chiller:Electric:EIR', 'Boiler:HotWater'],
      secondary: ['PlantLoop', 'Pump:VariableSpeed', 'Schedule:Compact'],
    },
    tags: ['reference-building', 'doe', 'medium-office', 'vav', 'benchmark'],
    stats: { zones: 18, surfaces: 150, hvacSystems: 3, plantLoops: 2, floorArea: 4982, objectCount: 550 },
    source: { origin: 'DOE', version: '24.1', author: 'PNNL' },
    learning: {
      summary: 'The DOE Reference Medium Office represents a typical three-story office with VAV HVAC serving multiple zones.',
      concepts: ['air-loops', 'plant-loops', 'internal-loads', 'sizing'],
      steps: [
        {
          title: 'Multi-Story Modeling',
          description: 'Three identical floors with perimeter and core zones.',
          focusObjects: ['Zone', 'BuildingSurface:Detailed'],
          tips: ['Zone multipliers can represent identical floors', 'Adiabatic between-floor surfaces'],
        },
        {
          title: 'VAV System',
          description: 'Multiple VAV air handling units serve the building.',
          focusObjects: ['AirLoopHVAC', 'AirTerminal:SingleDuct:VAV:Reheat'],
        },
        {
          title: 'Central Plant',
          description: 'Chiller and boiler serve all air handlers.',
          focusObjects: ['Chiller:Electric:EIR', 'Boiler:HotWater'],
        },
      ],
      relatedExamples: ['ref-small-office', '5zone-vav'],
    },
    idfUrl: '/examples/energyplus/RefBldgMediumOfficeNew2004_Chicago.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 85,
  },
  {
    id: 'ref-warehouse',
    name: 'DOE Reference Warehouse',
    filename: 'RefBldgWarehouseNew2004_Chicago.idf',
    description: 'DOE Commercial Reference Building - Warehouse. Large single-story with high bay storage and simple HVAC.',
    category: 'commercial',
    buildingType: 'warehouse',
    complexity: 'intermediate',
    hvacSystems: ['unitary'],
    objectsShowcased: {
      primary: ['ZoneHVAC:UnitHeater', 'Coil:Heating:Fuel', 'Zone'],
      secondary: ['People', 'Lights', 'ZoneInfiltration:DesignFlowRate'],
    },
    tags: ['reference-building', 'doe', 'warehouse', 'industrial', 'benchmark'],
    stats: { zones: 3, surfaces: 20, hvacSystems: 2, floorArea: 4835, objectCount: 180 },
    source: { origin: 'DOE', version: '24.1', author: 'PNNL' },
    learning: {
      summary: 'The DOE Reference Warehouse represents a typical large storage building with minimal conditioning.',
      concepts: ['zone-definition', 'hvac-basics', 'infiltration'],
      steps: [
        {
          title: 'High Bay Zones',
          description: 'Warehouses have high ceilings and large open spaces.',
          focusObjects: ['Zone'],
          tips: ['Zone volume affects infiltration calculations', 'Stratification can be significant'],
        },
        {
          title: 'Minimal HVAC',
          description: 'Unit heaters provide basic heating for storage areas.',
          focusObjects: ['ZoneHVAC:UnitHeater'],
          tips: ['Office areas may have separate systems', 'Loading docks often unconditioned'],
        },
      ],
      relatedExamples: ['ref-small-office', '5zone-air-cooled'],
    },
    idfUrl: '/examples/energyplus/RefBldgWarehouseNew2004_Chicago.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 72,
  },

  // ============ CONTROLS ============
  {
    id: 'demand-limiting',
    name: 'Demand Limiting with EMS',
    filename: '5ZoneAirCooledDemandLimiting.idf',
    description: 'Energy Management System example demonstrating demand limiting through load shedding.',
    category: 'controls',
    buildingType: 'office-small',
    complexity: 'expert',
    hvacSystems: ['packaged'],
    objectsShowcased: {
      primary: ['EnergyManagementSystem:Sensor', 'EnergyManagementSystem:Actuator', 'EnergyManagementSystem:Program'],
      secondary: ['EnergyManagementSystem:GlobalVariable', 'EnergyManagementSystem:OutputVariable'],
    },
    tags: ['ems', 'demand-limiting', 'load-shedding', 'controls', 'demand-response'],
    stats: { zones: 5, surfaces: 40, hvacSystems: 1, objectCount: 320 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Learn to use the Energy Management System (EMS) for custom control logic like demand limiting.',
      concepts: ['controls-ems', 'demand-response', 'output-variables'],
      steps: [
        {
          title: 'EMS Sensors',
          description: 'Sensors read values from the simulation for use in control logic.',
          focusObjects: ['EnergyManagementSystem:Sensor'],
          tips: ['Sensors connect to output variables', 'Can read from any simulation variable'],
        },
        {
          title: 'EMS Actuators',
          description: 'Actuators override default EnergyPlus behavior.',
          focusObjects: ['EnergyManagementSystem:Actuator'],
          tips: ['Check available actuators with Output:EnergyManagementSystem', 'Not all objects have actuators'],
        },
        {
          title: 'EMS Programs',
          description: 'Programs contain Erl code that runs at specified calling points.',
          focusObjects: ['EnergyManagementSystem:Program'],
          tips: ['Programs run in order at each calling point', 'Debug with Output:EnergyManagementSystem'],
        },
      ],
      commonMistakes: [
        'Wrong calling point for the desired behavior',
        'Actuator not found - check available actuators list',
        'Infinite loops in Erl code',
      ],
      relatedExamples: ['ems-custom-schedule', '5zone-air-cooled'],
    },
    idfUrl: '/examples/energyplus/5ZoneAirCooledDemandLimiting.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 70,
  },
  {
    id: 'ems-custom-schedule',
    name: 'EMS Custom Schedule',
    filename: 'EMSCustomSchedule.idf',
    description: 'Using EMS to create custom schedules based on simulation conditions.',
    category: 'controls',
    buildingType: 'office-small',
    complexity: 'expert',
    hvacSystems: ['vav'],
    objectsShowcased: {
      primary: ['EnergyManagementSystem:Program', 'EnergyManagementSystem:Actuator', 'Schedule:Constant'],
      secondary: ['EnergyManagementSystem:Sensor', 'EnergyManagementSystem:OutputVariable'],
    },
    tags: ['ems', 'schedules', 'custom-controls', 'advanced'],
    stats: { zones: 5, surfaces: 40, hvacSystems: 1, objectCount: 350 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Create dynamic schedules that respond to simulation conditions using EMS programming.',
      prerequisites: ['demand-limiting'],
      concepts: ['controls-ems', 'schedules'],
      steps: [
        {
          title: 'Schedule Actuators',
          description: 'Use EMS to override schedule values during simulation.',
          focusObjects: ['EnergyManagementSystem:Actuator'],
          tips: ['Schedule:Constant is easiest to actuate', 'Can create condition-based schedules'],
        },
        {
          title: 'Conditional Logic',
          description: 'Erl programs can implement complex conditional logic.',
          focusObjects: ['EnergyManagementSystem:Program'],
        },
      ],
      relatedExamples: ['demand-limiting', '5zone-vav'],
    },
    idfUrl: '/examples/energyplus/EMSCustomSchedule.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 65,
  },

  // ============ ENVELOPE & LIGHTING ============
  {
    id: 'window-tests',
    name: 'Window Glazing Tests',
    filename: 'WindowTests.idf',
    description: 'Comprehensive window and glazing examples demonstrating different modeling approaches.',
    category: 'envelope',
    buildingType: 'generic',
    complexity: 'intermediate',
    objectsShowcased: {
      primary: ['WindowMaterial:SimpleGlazingSystem', 'WindowMaterial:Glazing', 'Construction'],
      secondary: ['FenestrationSurface:Detailed', 'WindowProperty:FrameAndDivider'],
    },
    tags: ['windows', 'glazing', 'u-value', 'shgc', 'envelope'],
    stats: { zones: 1, surfaces: 10, objectCount: 150 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Learn different approaches to modeling windows from simple to detailed glazing specifications.',
      concepts: ['constructions', 'solar-gains', 'heat-balance'],
      steps: [
        {
          title: 'Simple Glazing',
          description: 'SimpleGlazingSystem specifies windows by U-factor and SHGC.',
          focusObjects: ['WindowMaterial:SimpleGlazingSystem'],
          tips: ['Good for early design', 'Limited accuracy for complex glazings'],
        },
        {
          title: 'Detailed Glazing',
          description: 'Full spectral properties for accurate window modeling.',
          focusObjects: ['WindowMaterial:Glazing', 'Construction'],
          tips: ['Use manufacturer data when available', 'Can model coatings and gas fills'],
        },
      ],
      relatedExamples: ['1zone-with-window', 'daylighting'],
    },
    idfUrl: '/examples/energyplus/WindowTests.idf',
    weatherFile: { name: 'Denver, CO (TMY3)', climateZone: '5B' },
    popularity: 75,
  },
  {
    id: 'daylighting',
    name: 'Daylighting Controls',
    filename: 'DaylightingDeviceTubular.idf',
    description: 'Daylighting with tubular daylighting devices (TDDs) - bringing daylight to interior spaces.',
    category: 'lighting',
    buildingType: 'office-small',
    complexity: 'intermediate',
    objectsShowcased: {
      primary: ['DaylightingDevice:Tubular', 'Daylighting:Controls', 'Daylighting:ReferencePoint'],
      secondary: ['Lights', 'Schedule:Compact'],
    },
    tags: ['daylighting', 'tdd', 'lighting-controls', 'energy-savings'],
    stats: { zones: 2, surfaces: 16, objectCount: 100 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Model tubular daylighting devices and electric lighting controls that respond to daylight availability.',
      concepts: ['daylighting', 'internal-loads', 'schedules'],
      steps: [
        {
          title: 'Tubular Daylighting Device',
          description: 'TDDs bring daylight from roof to interior spaces through reflective tubes.',
          focusObjects: ['DaylightingDevice:Tubular'],
          tips: ['Effective for single-story buildings', 'Dome type affects light capture'],
        },
        {
          title: 'Daylighting Controls',
          description: 'Controls dim or switch lights based on daylight illuminance.',
          focusObjects: ['Daylighting:Controls', 'Daylighting:ReferencePoint'],
          tips: ['Reference points should be at work plane height', 'Continuous dimming saves more energy'],
        },
      ],
      relatedExamples: ['window-tests', 'ref-small-office'],
    },
    idfUrl: '/examples/energyplus/DaylightingDeviceTubular.idf',
    weatherFile: { name: 'Denver, CO (TMY3)', climateZone: '5B' },
    popularity: 70,
  },

  // ============ ADVANCED ============
  {
    id: 'natural-ventilation',
    name: 'Natural Ventilation',
    filename: 'AirflowNetworkOccupantVentilationControl.idf',
    description: 'Natural ventilation using AirflowNetwork with occupant-controlled window opening.',
    category: 'advanced',
    buildingType: 'office-small',
    complexity: 'advanced',
    hvacSystems: ['natural-ventilation'],
    objectsShowcased: {
      primary: ['AirflowNetwork:SimulationControl', 'AirflowNetwork:MultiZone:Surface', 'AirflowNetwork:OccupantVentilationControl'],
      secondary: ['AirflowNetwork:MultiZone:Component:DetailedOpening', 'AirflowNetwork:MultiZone:Zone'],
    },
    tags: ['natural-ventilation', 'airflow-network', 'operable-windows', 'passive-cooling'],
    stats: { zones: 3, surfaces: 24, objectCount: 180 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Model natural ventilation using the AirflowNetwork with occupant-based window control.',
      concepts: ['natural-ventilation', 'infiltration', 'controls-ems'],
      steps: [
        {
          title: 'AirflowNetwork Setup',
          description: 'The AirflowNetwork calculates pressure-driven airflows through the building.',
          focusObjects: ['AirflowNetwork:SimulationControl'],
          tips: ['Wind pressure coefficients affect results significantly', 'Requires detailed opening geometry'],
        },
        {
          title: 'Occupant Control',
          description: 'Occupants open/close windows based on thermal comfort.',
          focusObjects: ['AirflowNetwork:OccupantVentilationControl'],
          tips: ['Based on thermal comfort models', 'Can include indoor air quality triggers'],
        },
      ],
      relatedExamples: ['5zone-air-cooled', 'demand-limiting'],
    },
    idfUrl: '/examples/energyplus/AirflowNetworkOccupantVentilationControl.idf',
    weatherFile: { name: 'San Francisco, CA (TMY3)', climateZone: '3C' },
    popularity: 72,
  },
  {
    id: 'plant-load-profile',
    name: 'Plant Load Profile',
    filename: 'PlantLoadProfile.idf',
    description: 'Using load profiles to represent building loads on plant equipment - useful for plant-only studies.',
    category: 'advanced',
    buildingType: 'generic',
    complexity: 'intermediate',
    plantEquipment: ['boiler', 'chiller'],
    objectsShowcased: {
      primary: ['LoadProfile:Plant', 'PlantLoop', 'Pump:VariableSpeed'],
      secondary: ['Boiler:HotWater', 'Chiller:Electric:EIR'],
    },
    tags: ['plant-loops', 'load-profile', 'simplified-modeling'],
    stats: { zones: 0, surfaces: 0, plantLoops: 2, objectCount: 80 },
    source: { origin: 'EnergyPlus', version: '24.1', author: 'DOE' },
    learning: {
      summary: 'Use load profiles to model plant equipment without detailed building modeling - great for plant optimization studies.',
      concepts: ['plant-loops', 'sizing'],
      steps: [
        {
          title: 'Load Profiles',
          description: 'LoadProfile:Plant objects impose specified loads on plant loops.',
          focusObjects: ['LoadProfile:Plant'],
          tips: ['Can use measured load data', 'Good for plant retrofit studies'],
        },
        {
          title: 'Plant Loop Setup',
          description: 'Plant loops connect equipment to load profiles.',
          focusObjects: ['PlantLoop', 'Pump:VariableSpeed'],
        },
      ],
      relatedExamples: ['5zone-vav', 'gshp'],
    },
    idfUrl: '/examples/energyplus/PlantLoadProfile.idf',
    weatherFile: { name: 'Chicago, IL (TMY3)', climateZone: '5A' },
    popularity: 65,
  },
];

/**
 * Get initial facet counts from the full example index.
 */
export function getInitialFacets(): ExampleFilterFacets {
  const categoryMap = new Map<string, number>();
  const buildingTypeMap = new Map<string, number>();
  const complexityMap = new Map<string, number>();
  const hvacSystemMap = new Map<string, number>();
  const plantEquipmentMap = new Map<string, number>();
  const conceptMap = new Map<string, number>();
  const tagMap = new Map<string, number>();

  for (const example of EXAMPLE_FILES) {
    categoryMap.set(example.category, (categoryMap.get(example.category) ?? 0) + 1);
    buildingTypeMap.set(example.buildingType, (buildingTypeMap.get(example.buildingType) ?? 0) + 1);
    complexityMap.set(example.complexity, (complexityMap.get(example.complexity) ?? 0) + 1);

    if (example.hvacSystems) {
      for (const system of example.hvacSystems) {
        hvacSystemMap.set(system, (hvacSystemMap.get(system) ?? 0) + 1);
      }
    }

    if (example.plantEquipment) {
      for (const equipment of example.plantEquipment) {
        plantEquipmentMap.set(equipment, (plantEquipmentMap.get(equipment) ?? 0) + 1);
      }
    }

    for (const concept of example.learning.concepts) {
      conceptMap.set(concept, (conceptMap.get(concept) ?? 0) + 1);
    }

    for (const tag of example.tags) {
      tagMap.set(tag, (tagMap.get(tag) ?? 0) + 1);
    }
  }

  return {
    categories: Array.from(categoryMap.entries())
      .map(([category, count]) => ({ category: category as any, count }))
      .sort((a, b) => b.count - a.count),
    buildingTypes: Array.from(buildingTypeMap.entries())
      .map(([buildingType, count]) => ({ buildingType: buildingType as any, count }))
      .sort((a, b) => b.count - a.count),
    complexityLevels: Array.from(complexityMap.entries())
      .map(([complexity, count]) => ({ complexity: complexity as any, count }))
      .sort((a, b) => {
        const order = ['beginner', 'intermediate', 'advanced', 'expert'];
        return order.indexOf(a.complexity) - order.indexOf(b.complexity);
      }),
    hvacSystems: Array.from(hvacSystemMap.entries())
      .map(([hvacSystem, count]) => ({ hvacSystem: hvacSystem as any, count }))
      .sort((a, b) => b.count - a.count),
    plantEquipment: Array.from(plantEquipmentMap.entries())
      .map(([equipment, count]) => ({ equipment: equipment as any, count }))
      .sort((a, b) => b.count - a.count),
    concepts: Array.from(conceptMap.entries())
      .map(([concept, count]) => ({ concept: concept as any, count }))
      .sort((a, b) => b.count - a.count),
    tags: Array.from(tagMap.entries())
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count),
  };
}

/**
 * Get the total number of example files.
 */
export function getExampleCount(): number {
  return EXAMPLE_FILES.length;
}
